import { Check } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getListColor } from "@/lib/listColors";

type FilterArea = {
  id: number;
  name: string;
  color: string;
};

type CheckFilterOption<T extends string> = {
  value: T;
  label: string;
  icon?: React.ReactNode;
};

type ChoiceFilterOption<T extends string> = {
  value: T;
  label: string;
  icon?: React.ReactNode;
};

type ToggleFilter = {
  label: string;
  checked: boolean;
  onToggle: () => void;
};

type ListFilter = {
  lists: FilterArea[];
  hiddenIds: Set<number>;
  onToggle: (id: number) => void;
};

type CheckFilter<T extends string> = {
  title: string;
  options: Array<CheckFilterOption<T>>;
  selected: Set<T>;
  onToggle: (value: T) => void;
};

type ChoiceFilter<T extends string> = {
  title: string;
  options: Array<ChoiceFilterOption<T>>;
  selected: T;
  onChoose: (value: T) => void;
  columns?: 1 | 2;
};

export default function TaskFilterDialog<
  TItem extends string = string,
  TStatus extends string = string,
  TRange extends string = string,
  TDoneAge extends string = string,
>({
  open,
  onOpenChange,
  title = "Filter",
  itemFilter,
  areaFilter,
  rangeFilter,
  statusFilter,
  doneAgeFilter,
  toggleFilters = [],
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  itemFilter?: CheckFilter<TItem>;
  areaFilter?: ListFilter;
  rangeFilter?: ChoiceFilter<TRange>;
  statusFilter?: CheckFilter<TStatus>;
  doneAgeFilter?: ChoiceFilter<TDoneAge>;
  toggleFilters?: ToggleFilter[];
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {itemFilter && <CheckFilterSection filter={itemFilter} />}
          {areaFilter && <ListFilterSection filter={areaFilter} />}
          {rangeFilter && <ChoiceFilterSection filter={rangeFilter} />}
          {statusFilter && <CheckFilterSection filter={statusFilter} />}
          {doneAgeFilter && <ChoiceFilterSection filter={doneAgeFilter} />}
          {toggleFilters.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Status</p>
              <div className="space-y-2">
                {toggleFilters.map(filter => (
                  <label
                    key={filter.label}
                    className="flex items-center gap-3 rounded-xl border border-border/60 px-3 py-2.5 text-sm"
                  >
                    <Checkbox checked={filter.checked} onCheckedChange={filter.onToggle} />
                    <span>{filter.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CheckFilterSection<T extends string>({ filter }: { filter: CheckFilter<T> }) {
  return (
    <div className="space-y-3">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{filter.title}</p>
      <div className="space-y-2">
        {filter.options.map(option => {
          const selected = filter.selected.has(option.value);
          return (
            <label
              key={option.value}
              className="flex items-center gap-3 rounded-xl border border-border/60 px-3 py-2.5 text-sm"
            >
              <Checkbox checked={selected} onCheckedChange={() => filter.onToggle(option.value)} />
              {option.icon}
              <span>{option.label}</span>
              {!selected && <span className="ml-auto text-xs text-muted-foreground">hidden</span>}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function ListFilterSection({ filter }: { filter: ListFilter }) {
  return (
    <div className="space-y-3">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Lists</p>
      <div className="space-y-2">
        {filter.lists.length === 0 ? (
          <p className="text-sm text-muted-foreground">No lists yet.</p>
        ) : (
          filter.lists.map(list => {
            const color = getListColor(list.color);
            const hidden = filter.hiddenIds.has(list.id);
            return (
              <label
                key={list.id}
                className="flex items-center gap-3 rounded-xl border border-border/60 px-3 py-2.5 text-sm"
              >
                <Checkbox checked={!hidden} onCheckedChange={() => filter.onToggle(list.id)} />
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: color.base }}
                  aria-hidden
                />
                <span>{list.name}</span>
                {hidden && <span className="ml-auto text-xs text-muted-foreground">hidden</span>}
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

function ChoiceFilterSection<T extends string>({ filter }: { filter: ChoiceFilter<T> }) {
  return (
    <div className="space-y-3">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{filter.title}</p>
      <div className={filter.columns === 1 ? "space-y-2" : "grid grid-cols-2 gap-2"}>
        {filter.options.map(option => (
          <button
            key={option.value}
            type="button"
            onClick={() => filter.onChoose(option.value)}
            className={`rounded-xl border px-3 py-2.5 text-left text-sm transition-colors ${
              filter.selected === option.value
                ? "border-foreground/50 bg-accent/50 font-semibold"
                : "border-border/60 hover:bg-accent/30"
            }`}
          >
            <span className="flex items-center gap-2">
              {option.icon}
              <span>{option.label}</span>
              {filter.selected === option.value && (
                <Check className="ml-auto h-4 w-4" aria-label="Selected" />
              )}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
